import {unionBy} from 'lodash';
import {
  FOLLOW_COLLEGE,
  UNFOLLOW_COLLEGE,
  LOAD_MORE_COLLEGES,
  LOAD_COLLEGES,
  RESET_PAGE,
  UPDATE_SORT_BY,
  UPDATE_SORT_ORDER,
  GET_SAVED_SORT,
  GET_USER_INFO,
  SET_USER_TYPE,
  GET_CITIZEN_NOTIFICATION,
  FINISHED_LOADING,
  UPDATE_COLLEGE_RELATIONSHIP,
  RECEIVE_TAGS,
} from 'src/redux/college-search/actions';
import {UserIdentifier} from 'src/types/enums';

interface EarningRelationship {
  statusType: string;
  deadlineDay?: string | number;
  deadlineMonth?: string | number;
  deadlineYear?: string | number;
}

// TODO: Combine with src/typs/store's College interface?
export interface CollegeSearchCollege {
  _id: string;
  name: string;
  city: string;
  state: string;
  logo?: string;
  logoUrl: string;
  banner?: string;
  slug: string;
  isFollowing?: boolean;
  amount?: number | string;
  amountLoading?: boolean;
  earningRelationship?: EarningRelationship;
  isPartner?: boolean;
  category?: string;
  earningsPossible?: boolean;
  totalUndergraduates?: number | string;
  acceptanceRate?: number | string;
  distance?: number;
  studentFacultyRatio?: number;
  majorsParticipation?: number;
  forbesRank?: number | string;
  usNewsRank?: number | string;
  highSchoolFollowerCount?: number;
  highSchoolApplicationStatusBreakdown?: Record<string, unknown>;
  studentSubmissionYear?: number;
  studentSubmissionTerm?: string;
  partnerSubmissionDeadlineMonth?: number;
  partnerSubmissionDeadlineDay?: number;
  partnerSubmissionDeadlineYear?: number;
  partnerSubmissionDeadlineTerm?: string;
}

interface Tag {
  _id: string;
  name: string;
  group?: string;
}

interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  dismissedCitizenNotification?: boolean;
  [key: string]: unknown;
}

interface CollegeSearchState {
  amountLoading: boolean;
  filteredColleges: CollegeSearchCollege[] | null;
  loading: boolean;
  page: number;
  tags: Tag[];
  totalColleges: number;
  user: User;
  userType: string;
  hasMore?: boolean;
  sortBy?: string;
  sortOrder?: string;
}

const defaultState: CollegeSearchState = {
  amountLoading: false,
  filteredColleges: null,
  loading: true,
  page: 0,
  tags: [],
  totalColleges: 0,
  user: {},
  userType: UserIdentifier.PUBLIC,
};

export const collegeSearchReducer = (
  state: CollegeSearchState = defaultState,
  action
): CollegeSearchState => {
  switch (action.type) {
    case FOLLOW_COLLEGE: {
      if (!state.filteredColleges) return state;

      const colleges = state.filteredColleges.map((college) => {
        const {id} = action;

        if (college._id === id) {
          return {
            ...college,
            amountLoading: true,
            isFollowing: true,
          };
        }
        return college;
      });

      return {
        ...state,
        filteredColleges: colleges,
      };
    }
    case UNFOLLOW_COLLEGE: {
      if (!state.filteredColleges) return state;

      const colleges = state.filteredColleges.map((college) => {
        if (college._id === action.id) {
          return {
            ...college,
            isFollowing: false,
          };
        }
        return college;
      });

      return {
        ...state,
        filteredColleges: colleges,
      };
    }
    case UPDATE_COLLEGE_RELATIONSHIP: {
      if (!state.filteredColleges) return state;

      const colleges = state.filteredColleges.map((college) => {
        const {
          id,
          college: {amount, earningRelationship},
        } = action;
        if (college._id === id) {
          return {
            ...college,
            amount: amount,
            amountLoading: false,
            earningRelationship,
          };
        }
        return college;
      });

      return {
        ...state,
        filteredColleges: colleges,
      };
    }
    case LOAD_MORE_COLLEGES:
      return {
        ...state,
        filteredColleges: unionBy(state.filteredColleges || [], action.loadedColleges, '_id'),
        hasMore: action.hasMore,
        page: state.page + 1,
        totalColleges: action.totalColleges,
      };
    case LOAD_COLLEGES:
      return {
        ...state,
        filteredColleges: action.loadedColleges,
        hasMore: action.hasMore,
        page: 1,
        totalColleges: action.totalColleges,
      };
    case RESET_PAGE:
      return {
        ...state,
        filteredColleges: null,
        page: 0,
      };
    case UPDATE_SORT_BY:
      return {
        ...state,
        sortBy: action.sort,
      };
    case UPDATE_SORT_ORDER:
      return {
        ...state,
        sortOrder: action.order,
      };
    case GET_SAVED_SORT: {
      const {sort, order} = action.search;
      return {
        ...state,
        sortBy: sort,
        sortOrder: order,
      };
    }
    case GET_USER_INFO:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.info,
        },
      };
    case SET_USER_TYPE:
      return {
        ...state,
        userType: action.userType,
      };
    case GET_CITIZEN_NOTIFICATION:
      return {
        ...state,
        user: {
          ...state.user,
          dismissedCitizenNotification: action.dismissed.dismissed,
        },
      };
    case FINISHED_LOADING:
      return {
        ...state,
        loading: action.loading,
      };
    case RECEIVE_TAGS:
      return {
        ...state,
        tags: action.tags,
      };
    default:
      return state;
  }
};
